import * as _ from 'lodash-es';
import type Quill from 'quill/core';
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { useDebouncedCallback } from 'use-debounce';
import type {
  MagneticWallHighlight,
  MagneticWallType,
} from '../../editor/editorSlice';
import useSyncMagneticWallStates from '../../editor/useSyncMagneticWallState';
import type { DocumentType } from '../../graphql/generated/gql';
import { gql } from '../../graphql/generated/gql';
import type { ExpandedPostModalAnnotation_AnnotationFragment } from '../../graphql/generated/graphql';
import useVisibleMagneticWallItems from '../useVisibleMagneticWallItems';
import ExpandedPostModalAnnotation from './ExpandedPostModalAnnotation';
import * as styles from './ExpandedPostModalMagneticWall.module.scss';

/**
 * Uses `offsetTop` instead of `getBoundingClientRect().top` to
 * ensure the top offset is calculated correctly when the modal opens
 * with an animation.
 */
const getRelativeOffsetTop = (
  element: HTMLElement,
  container: HTMLElement,
): number => {
  if (element === container || !container.contains(element)) {
    return 0;
  }

  const parentOffsetTop =
    element.offsetParent instanceof HTMLElement
      ? getRelativeOffsetTop(element.offsetParent, container)
      : 0;

  return element.offsetTop + parentOffsetTop;
};

const THREAD_ICON_HEIGHT = 28;
const THREAD_ICON_CENTER_OFFSET = -5;
const COMPACT_THRESHOLD = 50;

const POST_FRAGMENT = gql(`
  fragment ExpandedPostModalMagneticWall_post on Post {
    id
    annotations {
      id
      ...ExpandedPostModalAnnotation_annotation
    }
  }
`);

interface ExpandedPostModalMagneticWallProps {
  quill: Quill | null;
  post: DocumentType<typeof POST_FRAGMENT>;
}

const ExpandedPostModalMagneticWall = ({
  quill,
  post,
}: ExpandedPostModalMagneticWallProps) => {
  const [activeThread, setActiveThread] = useState<{
    id: string;
    type: MagneticWallType;
  } | null>(null);
  const [positions, setPositions] = useState<
    {
      thread: ExpandedPostModalAnnotation_AnnotationFragment;
      node: Element;
      top: number;
    }[]
  >([]);

  const ref = useRef<HTMLDivElement>(null);
  const [isCompact, setIsCompact] = useState(false);
  useLayoutEffect(() => {
    if (!ref.current) return undefined;

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry) {
        setIsCompact(entry.contentRect.width < COMPACT_THRESHOLD);
      }
    });
    observer.observe(ref.current);
    return () => {
      observer.disconnect();
    };
  }, []);

  const [magneticWallHighlight, setMagneticWallHighlight] =
    useState<MagneticWallHighlight | null>(null);

  useSyncMagneticWallStates({
    magneticWallHighlight,
    magneticWallFocus:
      activeThread == null
        ? null
        : {
            id: activeThread.id,
            type: activeThread.type,
            active: true,
            hasExpandIntent: false,
          },
  });

  useVisibleMagneticWallItems(
    post.annotations
      .filter((annotation) =>
        positions.some((p) => annotation.id === p.thread.id),
      )
      .map((annotation) => ({ id: annotation.id, type: 'annotation' })),
  );

  const updatePositions = useCallback(
    (localQuill: Quill) => {
      if (!localQuill) {
        setPositions([]);
        return;
      }

      const container = localQuill.root;

      const newPositions = _.compact(
        post.annotations.map((annotation) => {
          const node = container.querySelector(`.annotation-${annotation.id}`);
          if (!(node instanceof HTMLElement) || node.offsetParent === null) {
            return null;
          }
          return {
            node,
            thread: annotation,
            top:
              getRelativeOffsetTop(node, container) - THREAD_ICON_CENTER_OFFSET,
          };
        }),
      ).toSorted((a, b) => a.top - b.top);

      setPositions(
        newPositions.map((position, i) => {
          const prevPosition = newPositions[i - 1];
          return prevPosition
            ? {
                ...position,
                top: Math.max(
                  position.top,
                  Number(prevPosition.top) + THREAD_ICON_HEIGHT,
                ),
              }
            : position;
        }),
      );
    },
    [post.annotations],
  );

  const debouncedUpdatePositions = useDebouncedCallback(updatePositions, 200);

  useEffect(() => {
    if (!quill) return undefined;
    updatePositions(quill);
    debouncedUpdatePositions(quill);

    const listener = () => {
      debouncedUpdatePositions(quill);
    };

    quill.on('text-change', listener);
    return () => {
      quill.off('text-change', listener);
    };
  }, [updatePositions, debouncedUpdatePositions, quill]);

  useEffect(() => {
    if (!quill) return undefined;
    const listener = (e: MouseEvent) => {
      if (e.target instanceof Element) {
        const annotationNode = e.target.closest('[class*="annotation-"]');
        if (annotationNode) {
          for (const c of annotationNode.classList) {
            if (c.startsWith('annotation-')) {
              setActiveThread({
                id: c.replace(/^annotation-/, ''),
                type: 'annotation',
              });
              return;
            }
          }
        }
      }
      setActiveThread(null);
    };
    quill.root.addEventListener('click', listener);
    return () => {
      quill.root.removeEventListener('click', listener);
    };
  }, [quill]);

  useEffect(() => {
    if (!quill) return undefined;

    const observer = new ResizeObserver(() => debouncedUpdatePositions(quill));
    observer.observe(quill.root);

    return () => {
      observer.disconnect();
    };
  }, [debouncedUpdatePositions, quill]);

  return (
    <div ref={ref} className={styles.container}>
      {positions.map((position) => (
        <div
          key={position.thread.id}
          className={styles.annotation}
          style={{ top: position.top }}
          onMouseEnter={() => {
            setMagneticWallHighlight({
              id: position.thread.id,
              type: 'annotation',
            });
          }}
          onMouseLeave={() => {
            setMagneticWallHighlight(null);
          }}
        >
          <ExpandedPostModalAnnotation
            annotation={position.thread}
            compact={isCompact}
            activeThreadId={activeThread?.id ?? null}
            onActiveThreadIdChange={(id) =>
              setActiveThread(id ? { id, type: 'annotation' } : null)
            }
          />
        </div>
      ))}
    </div>
  );
};

export default ExpandedPostModalMagneticWall;
